import axios, { AxiosResponse, CancelTokenSource } from 'axios';
import { TripContext } from 'contexts';
import { DbState } from 'contexts/db-state';
import { useContext } from 'react';
import { donnieAndWalMartDemoLoad } from 'resources';
import { ChangeOperation, ChangeType, FailedRequestType, TripStatus, StopStatus, AppStateType } from 'services/enums';
import { AssignedTrip, AssignedTrips, CatchWeight, Company, CompleteStop, SyncStatus, LicensePlate, LicensePlateItem, LineItem, LineItemBarcode, Mispick, Order, OrderItem, OrderItemChange, OrderLicensePlate, Reasons, ReturnItem, Signature, Stop, StopEquipment, TrailerLocation, Trip, TripChange, TripUpdate, User, OrderItemChangeImage, ImageUpload } from 'services/types';
import { authentication, errorLogging, isOnline } from 'utils';
import { SyncingStatus } from './enums/syncingStatus';
import { useRequestCache } from './useRequestCache';
import { useSettingService } from './useSettingService';
import { useTripService } from './useTripService';
import { useImageUploadService } from './useImageUploadService';
import { StopEvent } from './enums/stopEvent';
import { CompanyEmail, CompanyEmailChange, CompanyEmailChangeSet } from './types/companyEmail';
import axiosInstance from 'utils/axiosConfig';
import {appInsights} from "../utils/appInsights";
import { envConfig } from "config"

const config = envConfig();


type DataMergeResult<T> = {
    newItems: T[],
    removedItems: T[]
}

type DataMergeOptions<T> = {
    isItemMergeable?: (item: T) => boolean,
    keyFields?: string[]
}

export const useLoadManagerService = () => {

    const { getTrainingMode, getTrainerId } = useSettingService()
    const { updateTrailerPadLocation, deleteTrip } = useTripService()
    const { cacheStopCompletion, sendCacheAppState } = useRequestCache()
    const { sendImagesToOnBase } = useImageUploadService()
    const { handleHttpError } = errorLogging()
    const { sendCapturedAppStateHoldersToAzure } = useRequestCache()

    const serviceUrl = `${config.REACT_APP_LOAD_MANAGER_SERVICE}`;
    const dynamanUrl = `${config.REACT_APP_DYNAMAN_API}`

    const tripContext = useContext(TripContext)

    let user: User | null = authentication.getUser()

    const mergeData = <T>(dbData: DbState<T>, oldData: T[], newData: T[], options?: DataMergeOptions<T>): DataMergeResult<T> => {
        const isItemMergeable: ((item: T) => boolean) | undefined = options?.isItemMergeable
        const doItemsMatch = (item1: any, item2: any): boolean => {
            const keyFields: string[] = options?.keyFields ?? ['id']
            let isMatch = true
            for (let i = 0; i < keyFields.length; i++) {
                const key = keyFields[i]
                if (item1[key] != item2[key]) {
                    isMatch = false
                    break
                }
            }

            return isMatch
        }

        const newItems: T[] = newData.filter((newItem: any) => !oldData.some((dbItem: any) => doItemsMatch(newItem, dbItem)))
        const lockedItems: T[] = isItemMergeable != null ? oldData.filter((dbItem: any) => !isItemMergeable(dbItem)) : []
        const updatedItems: T[] = newData.filter((newItem: any) => oldData.some((dbItem: any) => doItemsMatch(newItem, dbItem)) && !lockedItems.some((dbItem: any) => doItemsMatch(newItem, dbItem)))
        const removedItems: T[] = oldData.filter((dbItem: any) => !newData.some((newItem: any) => doItemsMatch(dbItem, newItem))
            && !lockedItems.some((lockedItem: any) => doItemsMatch(dbItem, lockedItem)))

        dbData.bulkPut([...updatedItems, ...newItems])
        dbData.bulkRemove(removedItems.map((item: any) => item.id))

        return { newItems, removedItems }
    }

    const removeSyncedTripsThatAreNotIncludedInTheAssignedTrips = (currentPayload: AssignedTrips): void => {

        tripContext.trips.forEach(trip => {

            var payLoadTrip = currentPayload.trips.find(x => x.id == trip.id);

            var stopIds = trip.stops != undefined ? trip.stops.map(x => x.id) : [];
            var syncStatus = tripContext.syncStatus.find(x => stopIds.includes(x.id));

            if (!payLoadTrip && !syncStatus) {
                deleteTrip(trip.id);
            }
        })
    }

    const updateTripupdated = (tripId: number, updated: Date) => {
        const trip = tripContext.trips.find(t => t.id === tripId)
        if (trip) {
            tripContext.trips.put({ ...trip, updated })
        }
    }

    const completeStopForNaNCompanyIds = (stop: Stop)=>{
        if(isNaN(+stop.companyId)){
            stop.stopStatus=StopStatus.Complete
        }        
    }

    const updateTripData = (tripData: AssignedTrips): TripChange[] => {
        //update updated regardless of pending status so duplicate updates don't occur
        tripData.trips.forEach(trip => updateTripupdated(trip.id, trip.updated))
        tripData.appointments.filter(s=>isNaN(+s.companyId)).forEach(stop=> completeStopForNaNCompanyIds(stop))

        //Pending means that the data being looked at is modifiable
        const isPendingTrip = (trip: Trip | undefined) =>  [TripStatus.Ready, TripStatus.Waiting, TripStatus.Importing].includes(trip?.status ?? TripStatus.Waiting),
            isPendingStop = (stop: Stop | undefined) => stop?.stopStatus === StopStatus.Pending,
            isPendingOrder = (order: Order | undefined) => isPendingStop(tripContext.stops.find(stop => stop.id === order?.stopId)),
            isPendingReturnItem = (returnItem: ReturnItem | undefined) => isPendingOrder(tripContext.orders.find(order => order.id === returnItem?.orderId)),
            isPendingLp = (lp: LicensePlate | undefined) => isPendingStop(tripContext.stops.find(stop => stop.id === lp?.stopId)),
            isPendingOrderItem = (orderItem: OrderItem | undefined) => isPendingOrder(tripContext.orders.find(order => order.id === orderItem?.orderId)),
            isPendingLineItem = (lineItem: LineItem | undefined) => !tripContext.orderItems.some(orderItem => orderItem.lineItemId === lineItem?.id && !isPendingOrderItem(orderItem))
                && !tripContext.returnItems.some(returnItem => returnItem.lineItemId === lineItem?.id && !isPendingReturnItem(returnItem)),
            isPendingLineItemBarcode = (lineItemBarcode: LineItemBarcode | undefined) => isPendingLineItem(tripContext.lineItems.find(li => li.id === lineItemBarcode?.lineItemId)),
            isPendingLpItem = (lpItem: LicensePlateItem | undefined) => isPendingLp(tripContext.licensePlates.find(lp => lp.id === lpItem?.licensePlateId)),
            isPendingOrderLp = (orderLp: OrderLicensePlate | undefined) => isPendingLp(tripContext.licensePlates.find(lp => lp.id === orderLp?.licensePlateId)),
            isPendingEquipment = (equipment: StopEquipment | undefined) => isPendingStop(tripContext.stops.find(stop => stop.id === equipment?.stopId)),
            isPendingCatchWeight = (catchWeight: CatchWeight | undefined) => isPendingOrderItem(tripContext.orderItems.find(oi => oi.id === catchWeight?.orderItemId)),
            isPendingCompany = (company: Company | undefined) => !tripContext.stops.some(stop => stop.companyId === company?.id && !isPendingStop(stop)),
            isPendingOrderItemChange = (change: OrderItemChange | undefined) => isPendingOrderItem(tripContext.orderItems.find(oi => oi.id === change?.orderItemId)),
            isPendingMispick = (mispick: Mispick | undefined) => isPendingStop(tripContext.stops.find(stop => stop.id === mispick?.stopId)),
            isPendingSignature = (signature: Signature | undefined) => {
                const orders: Order[] = tripContext.orders.filter(order => order.signatureId === signature?.id)
                const stops: Stop[] = tripContext.stops.filter(stop => orders.some(order => order.stopId === stop.id))
                const pendingTrips: Trip[] = tripContext.trips.filter(trip => stops.some(stop => trip.id === stop.tripId) && isPendingTrip(trip))
                return pendingTrips.length > 0
            },
            isPendingCompanyEmail = (companyEmail: CompanyEmail | undefined) => isPendingStop(tripContext.stops.find(stop => stop.companyId == companyEmail?.companyId))

        const trips: Trip[] = tripContext.trips.filter(t => tripData.trips.some(td => td.id === t.id)),
            stops: Stop[] = tripContext.stops.filter(s => trips.some(t => t.id === s.tripId)),
            orders: Order[] = tripContext.orders.filter(o => stops.some(s => s.id === o.stopId)),
            orderItems: OrderItem[] = tripContext.orderItems.filter(oi => orders.some(o => o.id === oi.orderId)),
            lineItems: LineItem[] = tripContext.lineItems.filter(li => orderItems.some(oi => oi.lineItemId === li.id) || tripContext.returnItems.some(ri => ri.lineItemId == li.id)),
            licensePlates: LicensePlate[] = tripContext.licensePlates.filter(lp => stops.some(s => s.id === lp.stopId))

        //these items can be merged using their "id" property
        mergeData(tripContext.trips, trips, tripData.trips, { isItemMergeable: isPendingTrip })
        const { newItems: newStops, removedItems: removedStops } = mergeData(tripContext.stops, stops, tripData.appointments, { isItemMergeable: isPendingStop })
        const { newItems: newOrders, removedItems: removedOrders } = mergeData(tripContext.orders, orders, tripData.orders, { isItemMergeable: isPendingOrder })
        mergeData(tripContext.licensePlates, licensePlates, tripData.licensePlates, { isItemMergeable: isPendingLp })
        mergeData(tripContext.orderItems, orderItems, tripData.orderItems, { isItemMergeable: isPendingOrderItem })
        mergeData(tripContext.lineItems, lineItems, tripData.lineItems, { isItemMergeable: isPendingLineItem })
        mergeData(tripContext.lineItemBarcodes, tripContext.lineItemBarcodes.filter(lib => lineItems.some(li => li.id === lib.lineItemId)), tripData.lineItemBarcodes, { isItemMergeable: isPendingLineItemBarcode })
        mergeData(tripContext.licensePlateItems, tripContext.licensePlateItems.filter(lpi => licensePlates.some(lp => lp.id === lpi.licensePlateId)), tripData.licensePlateItems, { isItemMergeable: isPendingLpItem })
        mergeData(tripContext.orderLicensePlates, tripContext.orderLicensePlates.filter(olp => licensePlates.some(lp => lp.id === olp.licensePlateId)), tripData.orderLicensePlates, { isItemMergeable: isPendingOrderLp })
        mergeData(tripContext.stopEquipments, tripContext.stopEquipments.filter(e => stops.some(s => s.id === e.stopId)), tripData.appointmentEquipment, { isItemMergeable: isPendingEquipment })
        mergeData(tripContext.companies, tripContext.companies.filter(c => stops.some(s => s.companyId === c.id)), tripData.companies, { isItemMergeable: isPendingCompany })
        mergeData(tripContext.returnItems, tripContext.returnItems.filter(ri => orders.some(o => o.id === ri.orderId)), tripData.returnItems, { isItemMergeable: isPendingReturnItem })

        //these items have temporary IDs assigned to them until they are inserted into the DB
        //when the DB data is pulled back later, need to match new DB ID to the temp ID on the device
        mergeData(tripContext.orderItemChanges, tripContext.orderItemChanges.filter(oic => orderItems.some(oi => oi.id === oic.orderItemId)), tripData.orderItemChanges, { isItemMergeable: isPendingOrderItemChange, keyFields: ['orderItemId', 'reconcileReasonId'] })
        mergeData(tripContext.catchWeights, tripContext.catchWeights.filter(cw => orderItems.some(oi => oi.id === cw.orderItemId)), tripData.catchWeights, { isItemMergeable: isPendingCatchWeight, keyFields: ['sequenceNumber', 'relativeRecordNumber'] })
        mergeData(tripContext.mispicks, tripContext.mispicks.filter(m => stops.some(s => s.id === m.stopId)), tripData.mispicks, { isItemMergeable: isPendingMispick, keyFields: ['stopId', 'lineItemId', 'quantity'] })
        mergeData(tripContext.signatures, tripContext.signatures.filter(s => orders.some(o => o.signatureId === s.id)), tripData.signatures, { isItemMergeable: isPendingSignature, keyFields: ['signatureImage'] })
        mergeData(
            tripContext.companyEmail,
            tripContext.companyEmail.filter(e => stops.some(s => s.companyId === e.companyId)),
            tripData.companyEmails,
            {
                isItemMergeable: isPendingCompanyEmail,
                keyFields: ['companyId', 'email', 'isPredelivery', 'isPostdelivery']
            }
        )


        const newStopChanges: TripChange[] = newStops.map(stop => ({
            tripId: stop.tripId,
            operation: ChangeOperation.Insert,
            type: ChangeType.Stop,
            value: stop
        }))

        const removedStopChanges: TripChange[] = removedStops.map(stop => ({
            tripId: stop.tripId,
            operation: ChangeOperation.Delete,
            type: ChangeType.Stop,
            value: stop
        }))

        const newOrderChanges: TripChange[] = newOrders.map(order => ({
            tripId: tripData.appointments.find(stop => stop.id === order.id)?.tripId ?? -1,
            operation: ChangeOperation.Insert,
            type: ChangeType.Order,
            value: order
        }))

        removeSyncedTripsThatAreNotIncludedInTheAssignedTrips(tripData)

        return [...newStopChanges, ...removedStopChanges, ...newOrderChanges]
    }

    const getLoadAssignmentsByDriver = async (driverId?: string): Promise<TripChange[]> => {
        const isTrainingMode = getTrainingMode(), trainerId = getTrainerId()
        let id = driverId ?? (isTrainingMode ? trainerId : user?.driverId)

        let changes: TripChange[] = []
        if (id != null) {
            let response: AxiosResponse<AssignedTrips> | void = await axiosInstance.get<AssignedTrips>(`${serviceUrl}/Trip/Assigned/${id}`, { headers: await authentication.getAuthHeader('application/json') })
                .catch((error) => {
                    handleHttpError(error)
                    throw error
                });
            if (response && response.data) {
                changes = updateTripData(response.data)
            }
        }

        return changes
    }

    const getTripupdated = async (tripId: number): Promise<TripUpdate> => {
        let response: AxiosResponse<TripUpdate> | void
        if (await isOnline()) {
            response = await axiosInstance.get<TripUpdate>(`${serviceUrl}/Trip/${tripId}/Updated`, { headers: await authentication.getAuthHeader('application/json') }).catch(handleHttpError)
        }
        else {
            throw new Error('Something went wrong')
        }

        return (response && response.data) ? response.data : {} as TripUpdate
    }

    const refreshTripById = async (tripId: number): Promise<TripChange[]> => {
        let response: AxiosResponse<AssignedTrip> | void = await axiosInstance.get<AssignedTrip>(`${serviceUrl}/Trip/${tripId}`, { headers: await authentication.getAuthHeader('application/json') }).catch(handleHttpError)
        let changes: TripChange[] = []
        if (response && response.data) {
            changes = await updateTripData({ trips: [response.data.trip], ...response.data })
        }
        else {
            throw new Error(`Could not refresh trip ${tripId}. ${response != null && `Response was: ${response?.status}, ${response?.statusText}`}`)
        }
        return changes
    }

    const getTrailerPadLocation = async (trip: Trip): Promise<Trip | undefined> => {
        let base64TrailerNumber = trip.trailerId && btoa(`DOTF#${trip.trailerId}`)
        let response: AxiosResponse<TrailerLocation> | void = await axiosInstance.get<TrailerLocation>(`${dynamanUrl}/v1.1/trailers/${trip.trailerDcLocation}/trailer-location/${base64TrailerNumber}`, { headers: await authentication.getAuthHeader('application/json') }).catch(handleHttpError)
        let trailerLocation: TrailerLocation = (response && response.data) ? response.data : {} as TrailerLocation
        return await updateTrailerPadLocation(trip.id, trailerLocation)
    }

    const updateReasons = async (): Promise<void> => {
        let response: AxiosResponse<Reasons> | void = await axiosInstance.get<Reasons>(`${serviceUrl}/Reason`, { headers: await authentication.getAuthHeader('application/json') }).catch(handleHttpError)
        if (response && response.data) {
            await Promise.all([
                tripContext.verifyMultipleItemsReasons.set(response.data.verifyMultipleItems),
                tripContext.reconcileReasons.set(response.data.reconcile),
                tripContext.refusalReasons.set(response.data.refusal),
                tripContext.equipmentShortReasons.set(response.data.equipmentShort),
                tripContext.returnChangeReasons.set(response.data.returnChange),
                tripContext.equipmentTypes.set(response.data.equipmentTypes)
            ])
        }
        else {
            throw new Error(`Could not able to update the reasons, please try after some time`)
        }
    }

    const clearReasons = async (): Promise<void> => {
        await Promise.all([
            tripContext.verifyMultipleItemsReasons.bulkRemove(tripContext.verifyMultipleItemsReasons.map(r => r.id)),
            tripContext.reconcileReasons.bulkRemove(tripContext.reconcileReasons.map(r => r.id)),
            tripContext.refusalReasons.bulkRemove(tripContext.refusalReasons.map(r => r.id)),
            tripContext.equipmentShortReasons.bulkRemove(tripContext.equipmentShortReasons.map(r => r.id)),
            tripContext.returnChangeReasons.bulkRemove(tripContext.returnChangeReasons.map(r => r.id)),
            tripContext.equipmentTypes.bulkRemove(tripContext.equipmentTypes.map(r => r.id)),
        ])
    }

    const completeStop = async (stopId: number): Promise<boolean> => {
        const stop: Stop = tripContext.stops.find(s => s.id === stopId) ?? {} as Stop
        const stopEquipments: StopEquipment[] = tripContext.stopEquipments.filter(s => s.stopId === stopId)
        const orders: Order[] = tripContext.orders.filter(o => o.stopId === stopId)
        const ordersWithoutSignaturesId: Order[] = orders.filter(order => order.signatureId == null)
        const orderItems: OrderItem[] = tripContext.orderItems.filter(item => orders.some(o => o.id == item.orderId))
        const orderItemChanges: OrderItemChange[] = tripContext.orderItemChanges.filter(change => orderItems.some(item => item.id == change.orderItemId))
        const catchWeights: CatchWeight[] = tripContext.catchWeights.filter(cw => orderItems.some(item => item.id == cw.orderItemId))
        const lps: LicensePlate[] = tripContext.licensePlates.filter(lp => lp.stopId === stopId)
        const signatures: Signature[] = tripContext.signatures.toArray() ?? []
        const ordersWithoutCorrectSignatureId: Order[] = orders.filter(order => order.signatureId != null && !signatures.some(s => s.id == order.signatureId)) 
        const signaturesForCurrentStop: Signature[] = signatures.filter(sign => orders.some(o => o.signatureId == sign.id))
        const mispicks: Mispick[] = tripContext.mispicks.filter(item => item.stopId === stopId)
            .map(item => {
                //remove unnecessary fields that sometimes causes firewall blocks
                delete item.longDescription;
                return item;
            })
        const returnItems: ReturnItem[] = tripContext.returnItems.filter(item => orders.some(o => o.id == item.orderId))
        const mapToCompanyEmailChange = (email: CompanyEmail): CompanyEmailChange => ({
            companyId: email.companyId,
            email: email.email,
            isPredelivery: email.isPredelivery,
            isPostdelivery: email.isPostdelivery,
        })

        const companyEmailAdditions: CompanyEmail[] = tripContext.companyEmailAdditions.filter(email => email.companyId === stop.companyId)
        const companyEmailDeletions: CompanyEmail[] = tripContext.companyEmailDeletions.filter(email => email.companyId === stop.companyId)

        const companyEmailChanges: CompanyEmailChangeSet = {
            additions: companyEmailAdditions.map(mapToCompanyEmailChange),
            deletions: companyEmailDeletions.map(mapToCompanyEmailChange),
        }
        
        //This will help us identify why orders are missing signatureId's or the wrong id gets assigned to the Order
        //We can remove this once those issues get fixed.
        if(stop.stopEvent === StopEvent.LUL && (ordersWithoutCorrectSignatureId.length > 0 || ordersWithoutSignaturesId.length > 0)) {
            const message: string = ordersWithoutCorrectSignatureId.length > 0 ? `Stop ${stop.id} is trying to complete with incorrect signatureIds associated with order(s)` : `Stop ${stop.id} is trying to complete with order(s) missing signatureIds`

            await sendCacheAppState(AppStateType.IssueReport, message)

            //TODO: This should eventually be removed once the main issue
            //of assigning the signature id to the order is fixed
            fixSignatureForOrder(ordersWithoutCorrectSignatureId)
            fixSignatureForOrder(ordersWithoutSignaturesId)
        }

        let data: CompleteStop = {
            appointment: stop,
            appointmentEquipment: stopEquipments,
            orders: orders,
            orderItems: orderItems,
            orderItemChanges: orderItemChanges,
            catchWeights: catchWeights,
            licensePlates: lps,
            //convert signature data to base64 to avoid WAF blocks
            signatures: signaturesForCurrentStop.map(signature => ({ ...signature, signatureImage: btoa(signature.signatureImage ?? '') })),
            mispicks: mispicks,
            returnItems: returnItems,
            companyEmailChanges: companyEmailChanges
        }
        
        try {
            
            appInsights.trackEvent({name: 'Caching Stop Completion Data', properties: {stopId}})
            await cacheStopCompletion(data)
            appInsights.trackEvent({name: 'Stop Completion Data Successfully Cached in Cosmos', properties: {stopId}})
            
        }
        catch (e) {
            const error = e as Error;
            appInsights.trackEvent({name: 'Cacheing Stop Completion Data failed', properties: {stopId}})
            appInsights.trackException({ exception: error, properties: { stopId } })
            console.error(`Documents could not be upserted: ${JSON.stringify(e)}`)
            handleHttpError(e)
        }

        let response: AxiosResponse<void> | undefined
        console.log('AppInsights: Sending Stop Completion To API', { stopId })
        appInsights.trackEvent({name: 'Sending Stop Completion To API', properties: {stopId}})
        try {
            response = await axiosInstance.post(`${serviceUrl}/stop/${stopId}/EnqueueCompletion`, data, { headers: await authentication.getAuthHeader('application/json') })
        } catch (err: any) {
            if (!err.response) {
                console.log('AppInsights: Stop Completion API Unavailable', { stopId })
                appInsights.trackEvent({ name: 'Stop Completion API Unavailable', properties: { stopId } })
            }
        
            console.log('AppInsights: Stop Completion Upload Failed', { stopId })
            appInsights.trackEvent({ name: 'Stop Completion Upload Failed', properties: { stopId } })
        
            handleHttpError(err)
            recordFailedRequest(stopId, data, SyncingStatus.FailedToSync, FailedRequestType.StopCompletion)
        }

        const existingFailedRequest = tripContext.syncStatus.find(f => f.type == FailedRequestType.StopCompletion && f.id == stopId)
        if (response && response.status === 200) {
            tripContext.companyEmailAdditions.bulkRemove(companyEmailAdditions.map(email => email.id))
            tripContext.companyEmailDeletions.bulkRemove(companyEmailDeletions.map(email => email.id))
            existingFailedRequest && tripContext.syncStatus.remove(existingFailedRequest.id!)
            return true
        } else if((response == undefined && existingFailedRequest != undefined) || (response && response.status != 200 && existingFailedRequest != undefined)) {
            if (response) {
                console.log('AppInsights: Stop Completion Upload Failed', { stopId, status: response.status })
                appInsights.trackEvent({
                    name: 'Stop Completion Upload Failed',
                    properties: { stopId, status: response.status }
                })
            }
            let oldStatus: SyncStatus | undefined = tripContext.syncStatus.find(x => x.id === stopId)
            oldStatus && tripContext.syncStatus.remove(oldStatus?.id!)
            recordFailedRequest(stopId, data, SyncingStatus.FailedToSync, FailedRequestType.StopCompletion)
            return false
        }
        else {
            existingFailedRequest == null && recordFailedRequest(stopId, data, SyncingStatus.FailedToSync, FailedRequestType.StopCompletion)
            return false
        }
    }

    const fixSignatureForOrder = (orders: Order[]): void => {
        for (const order of orders) {
            const signature = tripContext.signatures.find(x => x.stopId == order.stopId && x.temperatures?.includes(order.temperature))

            if (!signature) {
                console.error(`Order ${order.id} could not find matching signatures`)
                appInsights.trackEvent({name: 'Order Missing Signature', properties: {orderId: order.id}})
                continue
            }

            order.signatureId = signature.id
        }
    }

    const recordFailedRequest = (id: number, data: CompleteStop | OrderItemChangeImage[], status: SyncingStatus, type: FailedRequestType): void => {
        const existingFailedRequest = tripContext.syncStatus.find(f => f.type == type && f.data == id)
        existingFailedRequest == null && tripContext.syncStatus.put({ id: id, type: FailedRequestType.StopCompletion, data: data, status: SyncingStatus.FailedToSync})
    }

    const syncRequests = async (allRequests?: boolean): Promise<boolean> => {
        let oldStops
        let oldImages
        if (allRequests) {
            oldStops = tripContext.syncStatus.filter(request => request.status != null && request.type == FailedRequestType.StopCompletion)
            oldImages = tripContext.syncStatus.filter(request => request.status != null && request.type == FailedRequestType.ImageUpload)
        }
        else {
            oldStops = tripContext.syncStatus.filter(request => request.status == SyncingStatus.FailedToSync && request.type == FailedRequestType.StopCompletion)
            oldImages = tripContext.syncStatus.filter(request => request.status == SyncingStatus.FailedToSync && request.type == FailedRequestType.ImageUpload)
        }
        oldStops.forEach(oldStop => tripContext.syncStatus.put({id: oldStop.id, data: oldStop.data, status: SyncingStatus.Syncing, type: oldStop.type}))
        oldImages.forEach(image => tripContext.syncStatus.put({id: image.id, data: image.data, status: SyncingStatus.Syncing, type: image.type}))

        var capturedAppStateHoldersResults = await sendCapturedAppStateHoldersToAzure();

        let oldStopsResults = await Promise.all(oldStops && oldStops.filter(x => x.type == FailedRequestType.StopCompletion).map(async (request) => await completeStop(request.id as number)))
        let oldImageResults = await Promise.all(oldImages && oldImages.filter(x => x.type == FailedRequestType.ImageUpload).map(async (request) => await sendImagesToOnBase(request.data as OrderItemChangeImage[])))
            
        let results = [...oldStopsResults, ...oldImageResults]

        const areAllItemsSynced: boolean = results.every(successful => successful === true) && capturedAppStateHoldersResults;
        return areAllItemsSynced
    }

    const searchInventoryFetchResult = async (query: string, cancel: CancelTokenSource): Promise<LineItem[]> => {
        let response: AxiosResponse<LineItem[]> | void = await axiosInstance.get<LineItem[]>(` ${config.REACT_APP_LOAD_MANAGER_SERVICE}/LineItem/Search?query=${query}`, { cancelToken: cancel.token, headers: await authentication.getAuthHeader('application/json') })
            .catch(error => {
                axios.isCancel(error) ? console.log(`request canceled`) : handleHttpError(error); 
                appInsights.trackException({ exception: error, properties: { query } })
                throw error
            })
        return (response && response.data) ? response.data : []
    }

    const loadDemoLoad = async () => {
        updateTripData(donnieAndWalMartDemoLoad)
    }

    return {
        getLoadAssignments: getLoadAssignmentsByDriver, 
        getTripupdated: getTripupdated,
        refreshTripById: refreshTripById,
        updateReasons: updateReasons,
        getTrailerPadLocation: getTrailerPadLocation,
        completeStop: completeStop,
        searchInventoryFetchResult: searchInventoryFetchResult,
        syncRequests: syncRequests,
        loadDemoLoad: loadDemoLoad,
        updateTripData: updateTripData,
        clearReasons: clearReasons
    }
}
